/**
 * Bug Report side panel component.
 * Allows users to submit bug reports which create GitHub issues.
 *
 * @module components/ui/BugReportPanel
 */

import { useState, useEffect } from 'react';
import { Send, Bug, ExternalLink, CheckCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { SlidePanel } from './SlidePanel';
import { Input } from './Input';
import { Select } from './Select';
import { Button } from './Button';
import { bugReportService } from '@/services';
import { ApiException } from '@/services/httpClient';
import { useBugReportStore } from '@/stores/useBugReportStore';
import type { BugCategory, BugReportResponse } from '@/types';

const CATEGORY_OPTIONS = [
  { value: 'UI', label: 'Portal UI' },
  { value: 'API', label: 'API' },
  { value: 'SDK', label: 'SDK' },
  { value: 'Performance', label: 'Performance' },
  { value: 'Documentation', label: 'Documentation' },
  { value: 'Other', label: 'Other' },
];

/**
 * Bug Report side panel
 */
export function BugReportPanel() {
  const {
    isOpen,
    sandboxId: prefillSandboxId,
    traceId: prefillTraceId,
    closePanel,
  } = useBugReportStore();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedIssue, setSubmittedIssue] = useState<BugReportResponse | null>(null);

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<BugCategory>('UI');
  const [traceId, setTraceId] = useState('');
  const [sandboxId, setSandboxId] = useState('');

  // Validation state
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset form when panel opens, and apply prefill values
  useEffect(() => {
    if (isOpen) {
      setTitle('');
      setDescription('');
      setCategory('UI');
      setTraceId(prefillTraceId || '');
      setSandboxId(prefillSandboxId || '');
      setErrors({});
      setSubmittedIssue(null);
    }
  }, [isOpen, prefillSandboxId, prefillTraceId]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!title.trim()) {
      newErrors.title = 'Title is required';
    } else if (title.length < 5) {
      newErrors.title = 'Title must be at least 5 characters';
    } else if (title.length > 256) {
      newErrors.title = 'Title must be less than 256 characters';
    }

    if (!description.trim()) {
      newErrors.description = 'Description is required';
    } else if (description.length < 10) {
      newErrors.description = 'Description must be at least 10 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await bugReportService.submit({
        title: title.trim(),
        description: description.trim(),
        category,
        traceId: traceId.trim() || undefined,
        sandboxId: sandboxId.trim() || undefined,
      });

      setSubmittedIssue(response);
      toast.success('Bug report submitted successfully!');
    } catch (error) {
      if (error instanceof ApiException) {
        toast.error(error.detail || 'Failed to submit bug report');
      } else {
        toast.error('Failed to submit bug report');
      }
      console.error('[BugReportPanel] Error submitting bug report:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    closePanel();
    // Reset state after animation
    setTimeout(() => {
      setSubmittedIssue(null);
    }, 300);
  };

  // Success view content
  if (submittedIssue) {
    return (
      <SlidePanel
        isOpen={isOpen}
        onClose={handleClose}
        title="Bug Report"
        size="md"
        position="right"
      >
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-success-100 dark:bg-success-900/30 mb-4">
            <CheckCircle className="h-8 w-8 text-success-600 dark:text-success-400" />
          </div>

          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Bug Report Submitted
          </h2>

          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Thank you for reporting this issue! Your bug report has been created as GitHub issue #
            {submittedIssue.issueNumber}.
          </p>

          <a
            href={submittedIssue.issueUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 rounded-lg transition-colors mb-4"
          >
            <ExternalLink className="h-4 w-4" />
            View Issue on GitHub
          </a>

          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
        </div>
      </SlidePanel>
    );
  }

  return (
    <SlidePanel
      isOpen={isOpen}
      onClose={handleClose}
      title="Bug Report"
      description="Report issues to help us improve ADC"
      size="md"
      position="right"
      footer={
        <div className="flex gap-3">
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="flex items-center gap-2"
          >
            {isSubmitting ? (
              <>
                <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Send className="h-4 w-4" />
                Submit Bug Report
              </>
            )}
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Category */}
        <Select
          label="Category"
          options={CATEGORY_OPTIONS}
          value={category}
          onChange={(e) => setCategory(e.target.value as BugCategory)}
        />

        {/* Title */}
        <Input
          label="Title"
          placeholder="Brief description of the issue"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          error={errors.title}
          maxLength={256}
        />

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Please provide detailed information about the bug:&#10;- What were you trying to do?&#10;- What did you expect to happen?&#10;- What actually happened?&#10;- Steps to reproduce (if applicable)"
            rows={6}
            className={`w-full px-4 py-2.5 text-sm border rounded-lg bg-white dark:bg-[#161616] dark:text-white placeholder-gray-400 dark:placeholder-gray-600 transition-colors duration-200 focus:outline-none focus:ring-2 resize-y ${
              errors.description
                ? 'border-danger-500 focus:border-danger-500 focus:ring-danger-500/20'
                : 'border-gray-300 dark:border-[#2a2a2a] focus:border-primary-500 focus:ring-primary-500/20'
            }`}
          />
          {errors.description && (
            <p className="mt-1.5 text-sm text-danger-500 dark:text-danger-400">
              {errors.description}
            </p>
          )}
        </div>

        {/* Optional fields */}
        <div className="border-t border-gray-200 dark:border-[#2a2a2a] pt-5">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
            Optional: Debugging Information
          </p>
          <div className="space-y-4">
            <Input
              label="Trace ID"
              placeholder="e.g., abc123def456"
              value={traceId}
              onChange={(e) => setTraceId(e.target.value)}
              helperText="From API responses or SDK logs"
            />
            <Input
              label="Sandbox ID"
              placeholder="e.g., sandbox_abc123"
              value={sandboxId}
              onChange={(e) => setSandboxId(e.target.value)}
              helperText="If the issue is related to a sandbox"
            />
          </div>
        </div>

        {/* Help text */}
        <div className="p-4 bg-gray-50 dark:bg-[#0a0a0a] rounded-lg border border-gray-200 dark:border-[#2a2a2a]">
          <div className="flex items-start gap-3">
            <Bug className="h-5 w-5 text-gray-400 mt-0.5" />
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <p className="font-medium text-gray-900 dark:text-white mb-1">
                Tips for a good bug report:
              </p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li>Be specific about what went wrong</li>
                <li>Include steps to reproduce the issue</li>
                <li>Mention your browser and OS if relevant</li>
                <li>Include any error messages you see</li>
              </ul>
            </div>
          </div>
        </div>
      </form>
    </SlidePanel>
  );
}
